/**
 * Notification centre bell for the top bar.
 *
 * This is the batteries-included `NotificationBell` from
 * `@authe/notification-center` fed by the shared `ApiClient` notification source
 * (`useNotificationSource` — the `/notification_history` resource + live push).
 * The package owns the unread badge, mark-all-read, per-criticality list and
 * live high-severity toasts; we only inject the data port and the "view all"
 * route, so the bell and the Activity feed read the same backend with no
 * duplicated fetch code.
 */
import { useNavigate } from "@tanstack/react-router";
import { NotificationBell, type Notification } from "@authe/notification-center";
import { groupEventTargetId } from "@authe/fe-commons/events";
import { useNotificationPresenter, useNotificationSource } from "@/api/adapters";
import { ROUTES } from "@/config";

export function NotificationsMenu(): React.ReactElement {
  const navigate = useNavigate();
  const source = useNotificationSource();
  const present = useNotificationPresenter();

  // Deep-link a notification to the resource it's about. Cross-chain group
  // lifecycle events ("next leg arrived" / "complete") open the merged group
  // view; everything else falls back to the Activity feed.
  const onSelect = (notification: Notification): void => {
    const groupId = groupEventTargetId(notification.event);
    if (groupId) {
      navigate({ to: "/groups/$id", params: { id: groupId } });
      return;
    }
    navigate({ to: ROUTES.activity });
  };

  return (
    <NotificationBell
      source={source}
      present={present}
      persistKey="dashboard.notifications.read"
      onSelect={onSelect}
      onViewAll={() => navigate({ to: ROUTES.activity })}
    />
  );
}
